import { callPlusGraphQLDirect } from 'src/utilities/AWSServices';

const safeParse = (val) => {
    let parsed = typeof val === 'string' ? JSON.parse(val) : val;
    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
};

const executeGraphQL = async (query, variables = {}) => {
    const response = await callPlusGraphQLDirect(query, variables);
    const dataKey = Object.keys(response.data || {})[0];
    return safeParse(response.data[dataKey]);
};

export const getDocumentTreeWithTracks = async (filters) => {
    const qry = `query GetDocumentTreeWithTracks($filters: AWSJSON!) {
        getDocumentTreeWithTracks(filters: $filters)
    }`;
    return executeGraphQL(qry, { filters: JSON.stringify(filters) });
};

export const updateDocumentTreeTrack = async (input) => {
    const qry = `mutation UpdateDocumentTreeTrack($input: AWSJSON!) {
        updateDocumentTreeTrack(input: $input)
    }`;
    return executeGraphQL(qry, { input: JSON.stringify(input) });
};

export const updateDocumentTreeConfig = async (input) => {
    // Guard: prevent sending null/undefined association_id without a mapping_id (would create bad data)
    if (!input.mapping_id && !input.association_id) {
        console.error('updateDocumentTreeConfig: association_id is required when mapping_id is not provided', input);
        throw new Error('association_id is required when creating a new section mapping');
    }
    const qry = `mutation UpdateDocumentTreeConfig($input: AWSJSON!) {
        updateDocumentTreeConfig(input: $input)
    }`;
    return executeGraphQL(qry, { input: JSON.stringify(input) });
};

export const addDocumentTreeBlock = async (input) => {
    const qry = `mutation AddDocumentTreeBlock($input: AWSJSON!) {
        addDocumentTreeBlock(input: $input)
    }`;
    return executeGraphQL(qry, { input: JSON.stringify(input) });
};

export const getDocumentFromStore = async (filters) => {
    const qry = `query GetDocumentFromStore($filters: AWSJSON!) {
        getDocumentFromStore(filters: $filters)
    }`;
    return executeGraphQL(qry, { filters: JSON.stringify(filters) });
};

export const getDocumentBlocks = async (filters) => {
    const qry = `query GetDocumentBlocks($filters: AWSJSON!) {
        getDocumentBlocks(filters: $filters)
    }`;
    return executeGraphQL(qry, { filters: JSON.stringify(filters) });
};

export const listAllServicesForApp = async (appId) => {
    const qry = `query ListAllServicesForApp($app_id: String!) {
        listAllServicesForApp(app_id: $app_id)
    }`;
    return executeGraphQL(qry, { app_id: appId });
};

export const deleteDocumentTreeBlock = async (input) => {
    const qry = `mutation DeleteDocumentTreeBlock($input: AWSJSON!) {
        deleteDocumentTreeBlock(input: $input)
    }`;
    return executeGraphQL(qry, { input: JSON.stringify(input) });
};

export const getDocumentTree = async (filters) => {
    const qry = `query GetDocumentTree($filters: AWSJSON!) {
        getDocumentTree(filters: $filters)
    }`;
    return executeGraphQL(qry, { filters: JSON.stringify(filters) });
};

export const getWorkivaStatuses = async (filters) => {
    const qry = `query GetDocumentTreeWithTracks($filters: AWSJSON!) {
        getDocumentTreeWithTracks(filters: $filters)
    }`;
    return executeGraphQL(qry, { filters: JSON.stringify({ ...filters, action: 'GET_WORKIVA_STATUSES' }) });
};

export const updateWorkivaStatus = async (input) => {
    const qry = `mutation UpdateDocumentTreeTrack($input: AWSJSON!) {
        updateDocumentTreeTrack(input: $input)
    }`;
    return executeGraphQL(qry, { input: JSON.stringify({ ...input, action: 'UPDATE_WORKIVA_STATUS' }) });
};

export const getTickNTieSections = async (project) => {
    const qry = `query GetTickNTieSections($project: String!) {
        getTickNTieSections(project: $project)
    }`;
    const result = await executeGraphQL(qry, { project });
    return result?.items || result || [];
};

export const getTickNTieHistory = async (project, limit, nextToken) => {
    const qry = `query GetTickNTieHistory($project: String!, $limit: Int, $nextToken: String) {
        getTickNTieHistory(project: $project, limit: $limit, nextToken: $nextToken)
    }`;
    const variables = { project };
    if (limit) variables.limit = limit;
    if (nextToken) variables.nextToken = nextToken;
    const result = await executeGraphQL(qry, variables);
    return result || { items: [], nextToken: null };
};

export const batchGetTickNTieStatus = async (filters) => {
    const qry = `query BatchGetTickNTieStatus($filters: AWSJSON!) {
        batchGetTickNTieStatus(filters: $filters)
    }`;

    const BATCH_SIZE = 100;
    const keys = filters?.keys || [];

    // If keys fit within a single batch, execute directly
    if (keys.length <= BATCH_SIZE) {
        return executeGraphQL(qry, { filters: JSON.stringify(filters) });
    }

    // Split keys into chunks of BATCH_SIZE and execute simultaneously
    const batches = [];
    for (let i = 0; i < keys.length; i += BATCH_SIZE) {
        batches.push(keys.slice(i, i + BATCH_SIZE));
    }

    const batchPromises = batches.map(batch =>
        executeGraphQL(qry, { filters: JSON.stringify({ ...filters, keys: batch }) })
    );

    const results = await Promise.all(batchPromises);

    // Flatten all batch results into a single array
    return results.reduce((acc, result) => {
        const items = Array.isArray(result) ? result : [];
        return [...acc, ...items];
    }, []);
};

/**
 * Extracts aliases from a service object and returns them as a comma-separated string.
 * Works for both FRISCO (service_aliases) and Flexico (aliases) shapes.
 * @param {object} service - A FRISCO or Flexico service object
 * @returns {string} Comma-separated alias references
 */
export const getServiceReferences = (service) => {
    const aliases = service?.service_aliases || service?.aliases || [];
    return aliases.map(a => a.alias).filter(Boolean).join(', ');
};

/**
 * Extracts aliases from a service object and returns them as an array.
 * @param {object} service - A FRISCO or Flexico service object
 * @returns {string[]} Array of alias references
 */
export const getServiceReferencesArray = (service) => {
    const aliases = service?.service_aliases || service?.aliases || [];
    return aliases.map(a => a.alias).filter(Boolean);
};
